import { Injectable, Injector } from '@angular/core';
import { IResponse } from '../interfaces/response.interface';
import { MealGroup } from '../interfaces/meal-group.interface';
import { Subject } from 'rxjs';
import { AbstractService } from 'src/app/shared/util/abstract.service';
import { CallbackFunction } from 'src/app/shared/interface/callback-function.interface';
import { IErrorServerResponse } from 'src/app/shared/interface/error.server.response.interface';
import { Url } from 'src/app/shared/util/url.class';
import { Meal } from 'src/app/panel/catering/interfaces/meal.interface';

@Injectable()
export class MealsService extends AbstractService {
  public mealsGroup: MealGroup[] = [];
  public mealsGroupChanged: Subject<boolean> = new Subject();
  public mealGroupClicked: Subject<MealGroup> = new Subject<MealGroup>();
  public mealGroupPortitions: number[] = [];

  constructor(injector: Injector) {
    super(injector);
  }

  public getEventMeals(
    eventId: string,
    clb: CallbackFunction<IResponse<MealGroup[]>>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMeal');
    url.push('event');
    url.push(eventId);
    url.put('orderBy', 'index');
    url.put('orderDirection', 'ASC');
    return this._subscribe(this.requestService.get(url.serialize()), clb, error, complete);
  }

  public getMeal(
    id: string,
    clb: CallbackFunction<Meal>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMeal');
    url.push(id);
    return this._subscribe(this.requestService.get(url.serialize()), clb, error, complete);
  }

  public searchAllMeals(
    data: { searchTerm: string },
    clb: CallbackFunction<IResponse<Meal[]>>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMeal');
    url.push('search');

    this._subscribe(
      this.requestService.post(url.serialize(), { recipeTitle: data.searchTerm.toLowerCase() }),
      clb,
      error,
      complete
    );
  }

  public addEventMeal(
    data: { eventId: string; eventRecipeId: string; eventMealGroupId: string; portitions: number; index: number },
    clb: CallbackFunction<IResponse<MealGroup[]>>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMeal');

    this._subscribe(
      this.requestService.post(url.serialize(), {
        event: data.eventId.toString(),
        eventRecipe: data.eventRecipeId.toString(),
        eventMealGroup: data.eventMealGroupId.toString(),
        portitions: data.portitions.toString(),
        index: data.index.toString()
      }),
      clb,
      error,
      complete
    );
  }

  public updateEventMeal(
    data: { eventMealId?: string; eventMealGroupId?: string; portitions?: number; index?: number; status?: string },
    clb: CallbackFunction<IResponse<MealGroup[]>>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMeal');
    url.push(data.eventMealId.toString());

    this._subscribe(
      this.requestService.put(url.serialize(), {
        eventMealGroup: data.eventMealGroupId.toString(),
        portitions: data.portitions.toString(),
        index: data.index.toString()
      }),
      clb,
      error,
      complete
    );
  }

  public updateEventMealPortions(
    data: { eventMealId: string; eventMealGroupId: string; portitions: number },
    clb: CallbackFunction<IResponse<MealGroup[]>>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMeal');
    url.push(data.eventMealId.toString());

    this._subscribe(
      this.requestService.put(url.serialize(), {
        eventMealGroup: data.eventMealGroupId.toString(),
        portitions: data.portitions.toString()
      }),
      clb,
      error,
      complete
    );
  }

  public updateMealStatus(
    data: { status: string },
    eventMealId: string,
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMeal');
    url.push(eventMealId.toString());

    this._subscribe(this.requestService.put(url.serialize(), data), clb, error, complete);
  }

  public updateMealsOrder(
    data: any,
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMeal');
    url.push('index');
    url.push('update');
    this._subscribe(this.requestService.put(url.serialize(), data), clb, error, complete);
  }

  public updateMealSubgroupsOrder(
    data: any,
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMealSubgroup');
    url.push('index');
    url.push('update');
    this._subscribe(this.requestService.put(url.serialize(), data), clb, error, complete);
  }

  public updateMealGroupsOrder(
    data: any,
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMealGroup');
    url.push('index');
    url.push('update');
    this._subscribe(this.requestService.put(url.serialize(), data), clb, error, complete);
  }

  public updateMealGroup(
    groupId: string,
    data: any,
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMealGroup');
    url.push(groupId);
    this._subscribe(this.requestService.put(url.serialize(), data), clb, error, complete);
  }

  public addMealGroup(
    data: any,
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMealGroup');
    this._subscribe(this.requestService.post(url.serialize(), data), clb, error, complete);
  }

  public addEventMealFromRecipe(
    data: {
      eventId: string;
      recipeId: string;
      eventMealSubGroupId: string;
      eventMealGroupId: string;
      portitions: number;
      index: number;
    },
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMeal');
    url.push('createFromRecipe');
    url.push(data.recipeId);
    url.put('catering', true);

    this._subscribe(
      this.requestService.post(url.serialize(), {
        event: data.eventId.toString(),
        eventMealSubgroup: data.eventMealSubGroupId.toString(),
        eventMealGroup: data.eventMealGroupId.toString(),
        portitions: data.portitions.toString(),
        index: data.index.toString()
      }),
      clb,
      error,
      complete
    );
  }

  public deleteEventMeal(
    data: { mealId: string },
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMeal');
    url.push(data.mealId);
    this._subscribe(this.requestService.delete(url.serialize()), clb, error, complete);
  }

  public deleteMealGroup(
    data: { mealGroupId: string },
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMealGroup');
    url.push(data.mealGroupId);
    this._subscribe(this.requestService.delete(url.serialize()), clb, error, complete);
  }

  public updatePortionsForMealGroup(
    mealGroupId: string,
    data: any,
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMealGroup');
    url.push('updateEventMeals');
    url.push(mealGroupId);
    this._subscribe(this.requestService.put(url.serialize(), data), clb, error, complete);
  }

  public searchGroups(
    eventId: string,
    name: string,
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMealGroup');
    url.push('search');
    this._subscribe(this.requestService.post(url.serialize(), { event: eventId, name: name }), clb, error, complete);
  }

  public searchSubGroups(
    eventId: string,
    name: string,
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMealSubgroup');
    url.push('search');
    this._subscribe(this.requestService.post(url.serialize(), { event: eventId, name: name }), clb, error, complete);
  }

  public addSubGroup(
    eventId: string,
    name: string,
    eventMealGroupId: string,
    index: number,
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMealSubgroup');
    this._subscribe(
      this.requestService.post(url.serialize(), {
        event: eventId,
        name: name,
        eventMealGroup: eventMealGroupId,
        index: index.toString()
      }),
      clb,
      error,
      complete
    );
  }

  public addSubGroupMultiple(
    data: any,
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMealSubgroup');
    url.push('createMultiple');
    this._subscribe(this.requestService.post(url.serialize(), data), clb, error, complete);
  }

  public updateMealSubgroup(
    id: string,
    data: any,
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMealSubgroup');
    url.push(id);
    this._subscribe(this.requestService.put(url.serialize(), data), clb, error, complete);
  }

  public deleteMealSubgroup(
    id: string,
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventMealSubgroup');
    url.push(id);
    this._subscribe(this.requestService.delete(url.serialize()), clb, error, complete);
  }

  public copyGroupsFromDrinks(
    eventId: string,
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventDrinkGroup');
    url.push('event');
    url.push(eventId);
    url.push('createMealGroups');
    this._subscribe(this.requestService.post(url.serialize(), {}), clb, error, complete);
  }

  public copyGroupsFromEquipment(
    eventId: string,
    clb: CallbackFunction<any>,
    error?: (error: IErrorServerResponse) => void,
    complete?: () => void
  ) {
    const url: Url = this.urlService.baseApiUrl;
    url.push('eventCatering');
    url.push('eventEquipmentGroup');
    url.push('event');
    url.push(eventId);
    url.push('createMealGroups');
    this._subscribe(this.requestService.post(url.serialize(), {}), clb, error, complete);
  }
}
